import React, { useState } from "react";
import { useData } from "../../context/DataContext";
import { useNavigate } from "react-router-dom";
import ImageUploadField from "../../utils/ImageUploadField";
import { successToast, errorToast, confirmDelete } from "../../utils/dialog";

export default function AdminTestimonialsManager() {
  const { reviews, addReview, updateReview, deleteTestimonial } = useData();
  const [visibleCount, setVisibleCount] = useState(6);
  const [formData, setFormData] = useState({
    name: "",
    review: "",
    phone: "",
    email: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [editingReview, setEditingReview] = useState(null);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setImageFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.review ||
      !formData.phone ||
      !formData.email
    ) {
      errorToast("Please fill in all required fields.");
      return;
    }

    try {
      if (editingReview) {
        await updateReview(editingReview.id, formData, imageFile);
        successToast("Review updated.");
      } else {
        await addReview(formData, imageFile);
        successToast("Review added.");
      }
      resetForm();
    } catch (error) {
      console.error("Error submitting review:", error);
      errorToast("An error occurred. Please try again.");
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    setFormData({
      name: review.name,
      review: review.review,
      phone: review.phone,
      email: review.email,
    });
    setImageFile(null);
    setExistingImageUrl(review.imageUrl || "");
  };

  const resetForm = () => {
    setFormData({ name: "", review: "", phone: "", email: "" });
    setImageFile(null);
    setExistingImageUrl("");
    setEditingReview(null);
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete Testimonial?",
        text: "This review will be permanently deleted.",
      })
    ) {
      try {
        await deleteTestimonial(id);
        successToast("Review deleted successfully.");
      } catch (err) {
        console.error(err);
        errorToast("Error deleting review.");
      }
    }
  };

  const handleSeeMore = () => {
    setVisibleCount((prev) => prev + 3);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex justify-start">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
        >
          ← Back
        </button>
      </div>

      <h2 className="text-3xl font-bold text-gray-800 text-center">
        Admin Testimonials Manager
      </h2>

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-700">
            {editingReview ? "Edit Review" : "Add New Review"}
          </h3>
          {editingReview && (
            <button
              onClick={resetForm}
              className="text-sm text-red-500 hover:underline"
            >
              Cancel Edit
            </button>
          )}
        </div>
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter Name"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone *
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter Phone Number"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter Email"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            />
          </div>
          <div>
            <ImageUploadField
              label="Avatar Image"
              hint="Recommended size: 200×200px (square)"
              aspect="square"
              previewUrl={existingImageUrl}
              file={imageFile}
              onChange={handleFileChange}
              onClear={() => {
                setImageFile(null);
                setExistingImageUrl("");
              }}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review *
            </label>
            <textarea
              name="review"
              value={formData.review}
              onChange={handleInputChange}
              placeholder="Write a Review"
              rows={4}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            ></textarea>
          </div>
          <div className="md:col-span-2 flex justify-center gap-4">
            <button
              type="submit"
              className="bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] px-6 py-2 rounded-lg transition font-medium"
            >
              {editingReview ? "Update Review" : "Add Review"}
            </button>
            {editingReview && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.slice(0, visibleCount).map((review) => (
          <div
            key={review.id}
            onClick={() => handleEdit(review)}
            className="bg-white p-4 rounded-lg shadow-md cursor-pointer hover:shadow-lg transition"
          >
            <div className="flex items-center gap-4 mb-4">
              <img
                src={review.imageUrl || "https://via.placeholder.com/80"}
                alt={review.name}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h4 className="text-lg font-semibold text-gray-800">
                  {review.name}
                </h4>
                <p className="text-sm text-gray-500">{review.email}</p>
              </div>
            </div>
            <p className="text-sm text-gray-700">{review.review}</p>
            <div className="flex justify-end gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => handleDelete(review.id)}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded transition cursor-pointer"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {visibleCount < reviews.length && (
        <div className="flex justify-center">
          <button
            onClick={handleSeeMore}
            className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition"
          >
            See More
          </button>
        </div>
      )}
    </div>
  );
}
